import {
  ArrowLeftOnRectangleIcon,
  ChatBubbleLeftRightIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  GlobeAltIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Link, usePage } from "@inertiajs/react";
import ScoutMark from "@/components/ScoutMark";
import type { SharedProps } from "@/types";

type SidebarProps = {
  open: boolean;
  onClose: () => void;
};

type NavItemProps = {
  href: string;
  label: string;
  icon: typeof GlobeAltIcon;
};

export default function Sidebar({ open, onClose }: SidebarProps) {
  const { paid_plan: paidPlan } = usePage<SharedProps>().props;

  return (
    <div>
      {open ? (
        <button
          type="button"
          aria-label="Close navigation menu"
          className="fixed inset-0 z-[60] bg-black/50 md:hidden"
          onClick={onClose}
        />
      ) : null}

      <aside
        id="sidebar"
        className={[
          "fixed inset-y-0 left-0 top-0 z-[70] h-dvh transform transition-transform duration-300 ease-in-out md:sticky md:z-30 md:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full",
        ].join(" ")}
      >
        <div className="flex h-full w-56 flex-col border-r border-gray-200 bg-gray-25">
          <div className="flex items-center justify-between px-5 py-4">
            <Link
              href="/domains"
              className="flex items-center gap-2 font-sans text-lg font-semibold text-primary"
              onClick={onClose}
            >
              <ScoutMark variant="icon" className="h-6 w-6 shrink-0" />
              ScoutUX
            </Link>
            <button type="button" aria-label="Close navigation menu" className="md:hidden" onClick={onClose}>
              <XMarkIcon className="h-5 w-5 text-gray-500" aria-hidden="true" />
            </button>
          </div>

          <div className="flex grow flex-col overflow-y-auto px-2 pb-3">
            <nav className="flex-1 space-y-0.5" aria-label="Main navigation">
              <NavItem href="/domains" label="Domains" icon={GlobeAltIcon} />
              <NavItem href="/subscription" label="Subscription" icon={CreditCardIcon} />
              {paidPlan ? <NavItem href="/settings" label="Settings" icon={Cog6ToothIcon} /> : null}
              <NavItem href="/contact" label="Contact" icon={ChatBubbleLeftRightIcon} />
            </nav>

            <div className="mt-auto border-t border-gray-200 pt-2">
              <Link
                href="/logout"
                method="delete"
                as="button"
                className="flex w-full cursor-pointer items-center rounded-md px-3 py-2 text-sm text-gray-500 hover:text-gray-800"
              >
                <ArrowLeftOnRectangleIcon className="mr-3 h-4 w-4" aria-hidden="true" />
                Logout
              </Link>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

function NavItem({ href, label, icon: Icon }: NavItemProps) {
  const { url } = usePage();
  const active = url === href || url.startsWith(`${href}/`);

  return (
    <Link
      href={href}
      className={[
        "flex items-center rounded-md px-3 py-2 text-base",
        active ? "bg-primary-50 font-medium text-primary" : "text-gray-600 hover:bg-white hover:text-gray-900",
      ].join(" ")}
    >
      <Icon className={["mr-3 h-4 w-4", active ? "" : "text-gray-400"].join(" ")} aria-hidden="true" />
      {label}
    </Link>
  );
}
